import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";

// Pasos del tour principal (sidebar)
const sidebarSteps = [
  {
    target: '[data-testid="empresa-selector"]',
    title: "🏢 Selector de Empresa",
    content: "Aquí puedes cambiar entre las diferentes empresas que tienes registradas. Al seleccionar una empresa, se cargarán automáticamente sus ejercicios contables.",
    position: "bottom",
  },
  {
    target: '[data-testid="ejercicios-menu"]',
    title: "📅 Análisis por ejercicio",
    content: "Abre el desplegable para elegir un año: Dashboard (KPIs y ratios), Balance, PyG, EBITDA, Flujos y Margen Bruto, y la revisión de cuentas pendientes.",
    position: "bottom",
  },
  {
    target: '[data-testid="comparativa-link"]',
    title: "📊 Comparativas",
    content: "Compara varios ejercicios (históricos y proyectados): ratios, años y flujos de caja, con gráficos de evolución.",
    position: "bottom",
  },
  {
    target: '[data-testid="gestion-empresa-link"]',
    title: "⚙️ Administración",
    content: "Desde aquí gestionas la empresa (ejercicios e importación de Sumas y Saldos), las exportaciones y las integraciones con Finoview y Holded.",
    position: "bottom",
  },
  {
    target: '[data-testid="tour-button"]',
    title: "❓ Ayuda",
    content: "Puedes volver a ver este tour en cualquier momento haciendo clic en este botón.",
    position: "bottom",
  },
  {
    target: '[data-testid="theme-toggle"]',
    title: "🌓 Cambiar Tema",
    content: "Alterna entre el cristal claro y el cristal oscuro. Los datos son los mismos; solo cambia el aspecto.",
    position: "bottom",
  },
];

// Pasos para la página de Gestión de Empresa
const gestionEmpresaSteps = [
  {
    target: '[data-testid="empresa-info-card"]',
    title: "📋 Información de la Empresa",
    content: "Aquí puedes ver los datos de la empresa: nombre, NIF y otros detalles. Usa los botones de acción para editar o gestionar.",
    position: "bottom",
  },
  {
    target: '[data-testid="ejercicios-list"]',
    title: "📅 Lista de Ejercicios",
    content: "Todos los ejercicios contables de la empresa. Puedes ver el estado de cada uno, el número de cuentas importadas y acceder a sus informes.",
    position: "top",
  },
  {
    target: '[data-testid="new-ejercicio-btn"]',
    title: "➕ Nuevo Ejercicio",
    content: "Crea un nuevo ejercicio contable para esta empresa. Después podrás importar el archivo de Sumas y Saldos.",
    position: "bottom",
  },
];

// Pasos para la página de Comparativa
const comparativaSteps = [
  {
    target: '[data-testid="year-selector"]',
    title: "📅 Selección de Años",
    content: "Selecciona los años que quieres comparar. Puedes seleccionar varios ejercicios para ver su evolución.",
    position: "bottom",
  },
  {
    target: '[data-testid="export-pv-btn"]',
    title: "📄 Exportar PdV",
    content: "Exporta los datos en formato Plantilla de Ventas (PdV), útil para importar en otras aplicaciones.",
    position: "bottom",
  },
  {
    target: '[data-testid="export-comparativa-btn"]',
    title: "📊 Exportar Comparativa",
    content: "Genera un Excel con todas las hojas (Balance, PyG, Dashboard) comparando los años seleccionados en columnas.",
    position: "bottom",
  },
  {
    target: '[data-testid="evolution-chart"]',
    title: "📈 Gráfico de Evolución",
    content: "Visualiza la evolución de ventas, EBITDA y resultado neto a lo largo de los años.",
    position: "top",
  },
  {
    target: '[data-testid="ratios-chart"]',
    title: "📊 Evolución de Ratios",
    content: "Compara la evolución de ROA, ROE y Margen EBITDA entre ejercicios.",
    position: "top",
  },
];

export function GuidedTour() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [steps, setSteps] = useState(sidebarSteps);

  // Determinar qué pasos mostrar según la página actual
  const getStepsForCurrentPage = useCallback(() => {
    const path = location.pathname;
    
    if (path.includes('/comparativa')) {
      return comparativaSteps;
    } else if (path.match(/\/empresas\/[^/]+$/) && !path.includes('/comparativa')) {
      return gestionEmpresaSteps;
    }
    return sidebarSteps;
  }, [location.pathname]);

  // Escuchar evento para iniciar el tour
  useEffect(() => {
    const handleStartTour = () => {
      const newSteps = getStepsForCurrentPage();
      setSteps(newSteps);
      setCurrentStep(0);
      setIsOpen(true);
    };

    window.addEventListener('startTour', handleStartTour);
    return () => window.removeEventListener('startTour', handleStartTour);
  }, [getStepsForCurrentPage]);

  // Check if first visit - only show sidebar tour on main pages
  useEffect(() => {
    const hasSeenTour = localStorage.getItem("finreport_tour_completed");
    const path = location.pathname;
    const isMainPage = path === '/' || path === '/inicio' || path === '/empresas' || path.match(/^\/ejercicios\/[^/]+\/(dashboard|balance|pyg)$/);
    
    if (!hasSeenTour && isMainPage) {
      const timer = setTimeout(() => {
        setSteps(sidebarSteps);
        setCurrentStep(0);
        setIsOpen(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Update target position and scroll element into view if needed
  const updateTargetPosition = useCallback(() => {
    if (!isOpen || !steps[currentStep]) return;
    
    const step = steps[currentStep];
    const element = document.querySelector(step.target);
    
    if (element) {
      // First, scroll the element into view if it's not visible
      const rect = element.getBoundingClientRect();
      const isInViewport = 
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth;
      
      if (!isInViewport) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        // Wait a bit for scroll to complete, then update position
        setTimeout(() => {
          const newRect = element.getBoundingClientRect();
          setTargetRect({
            top: newRect.top,
            left: newRect.left,
            right: newRect.right,
            bottom: newRect.bottom,
            width: newRect.width,
            height: newRect.height,
          });
        }, 300);
      } else {
        setTargetRect({
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height,
        });
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep, isOpen, steps]);

  useEffect(() => {
    // Initial update with a small delay to let the DOM settle
    const initialTimeout = setTimeout(updateTargetPosition, 100);
    
    // Update frequently for smooth tracking
    const interval = setInterval(updateTargetPosition, 150);
    
    // Listen for resize and scroll events
    window.addEventListener("resize", updateTargetPosition);
    window.addEventListener("scroll", updateTargetPosition, true);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      window.removeEventListener("resize", updateTargetPosition);
      window.removeEventListener("scroll", updateTargetPosition, true);
    };
  }, [updateTargetPosition]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      // Force immediate position update
      setTimeout(updateTargetPosition, 50);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Force immediate position update
      setTimeout(updateTargetPosition, 50);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("finreport_tour_completed", "true");
  };

  const getTooltipPosition = () => {
    if (!targetRect) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }
    
    const step = steps[currentStep];
    const padding = 20;
    const tooltipWidth = 350;
    const tooltipHeight = 200;
    
    switch (step.position) {
      case "right":
        return {
          top: Math.max(padding, Math.min(window.innerHeight - tooltipHeight - padding, targetRect.top + targetRect.height / 2 - tooltipHeight / 2)),
          left: Math.min(window.innerWidth - tooltipWidth - padding, targetRect.right + padding),
        };
      case "left":
        return {
          top: Math.max(padding, targetRect.top + targetRect.height / 2 - tooltipHeight / 2),
          left: Math.max(padding, targetRect.left - tooltipWidth - padding),
        };
      case "top":
        return {
          top: Math.max(padding, targetRect.top - tooltipHeight - padding),
          left: Math.max(padding, Math.min(window.innerWidth - tooltipWidth - padding, targetRect.left + targetRect.width / 2 - tooltipWidth / 2)),
        };
      case "bottom":
        return {
          top: Math.min(window.innerHeight - tooltipHeight - padding, targetRect.bottom + padding),
          left: Math.max(padding, Math.min(window.innerWidth - tooltipWidth - padding, targetRect.left + targetRect.width / 2 - tooltipWidth / 2)),
        };
      default:
        return {
          top: targetRect.bottom + padding,
          left: targetRect.left,
        };
    }
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const tooltipPos = getTooltipPosition();

  return (
    <div className="fixed inset-0 z-[9998]">
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Spotlight on target element */}
      {targetRect && (
        <div
          className="absolute border-2 border-[#F6AA19] rounded-lg transition-all duration-300"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6), 0 0 0 4px rgba(246, 170, 25, 0.3), 0 0 20px rgba(246, 170, 25, 0.5)",
          }}
        />
      )}
      
      {/* Tooltip */}
      <Card
        className="fixed z-[9999] w-[350px] shadow-2xl border-2 border-[#F6AA19]/30 animate-in fade-in slide-in-from-bottom-4 duration-300"
        style={tooltipPos}
      >
        <CardContent className="p-5">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          
          {/* Step indicator */}
          <div className="flex items-center gap-1.5 mb-3">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  idx === currentStep
                    ? "w-6 bg-[#F6AA19]"
                    : idx < currentStep
                    ? "w-1.5 bg-[#F6AA19]/50"
                    : "w-1.5 bg-muted"
                )}
              />
            ))}
          </div>
          
          {/* Content */}
          <h3 className="font-bold text-lg mb-2">{step?.title}</h3>
          <p className="text-muted-foreground text-sm leading-relaxed mb-4">
            {step?.content}
          </p>
          
          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-muted-foreground"
            >
              Saltar
            </Button>
            
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Anterior
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className="bg-[#F6AA19] text-[#31313D] hover:bg-[#F6AA19]/90"
              >
                {currentStep === steps.length - 1 ? (
                  "Finalizar"
                ) : (
                  <>
                    Siguiente
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default GuidedTour;
